"use client"

import { useState, useEffect } from "react"
import { db } from "@/lib/firebase"
import { collection, query, orderBy, getDocs, doc, updateDoc, deleteDoc, where } from "firebase/firestore"
import { motion } from "framer-motion"
import { Search, Mail, Calendar, Shield, Trash2, User, Loader2, ShieldCheck, UserX, SearchIcon, Eye, EyeOff } from "lucide-react"
import { toast } from "sonner"

export default function UsersManager() {
  const [users, setUsers] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState("")

  useEffect(() => {
    fetchUsers()
  }, [])

  const fetchUsers = async () => {
    try {
      const q = query(collection(db, "users"))
      const snapshot = await getDocs(q)
      const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
      // Sort in-memory to ensure users without createdAt still appear
      const sorted = data.sort((a, b) => {
        const timeA = a.createdAt?.seconds || 0
        const timeB = b.createdAt?.seconds || 0
        return timeB - timeA
      })
      setUsers(sorted)
    } catch (error) {
      console.error(error)
      toast.error("Failed to fetch users")
    } finally {
      setLoading(false)
    }
  }

  const toggleAdmin = async (user: any) => {
    if (!confirm(`Are you sure you want to change ${user.name}'s role?`)) return
    const newRole = user.role === 'admin' ? 'student' : 'admin'
    try {
      await updateDoc(doc(db, "users", user.id), { role: newRole })
      toast.success(`${user.name} is now a ${newRole}`)
      fetchUsers()
    } catch (error) {
      toast.error("Role update failed")
    }
  }

  const toggleVisibility = async (user: any) => {
    try {
      const newStatus = user.isVisible === false ? true : false
      await updateDoc(doc(db, "users", user.id), { isVisible: newStatus })
      toast.success(newStatus ? "User record is now active/visible" : "User record is now hidden/inactive")
      fetchUsers()
    } catch (error) {
      toast.error("Failed to update status")
    }
  }

  const deleteUser = async (user: any) => {
    if (!confirm("Are you sure? This only deletes the record, not the Auth account.")) return
    try {
      await deleteDoc(doc(db, "users", user.id))
      toast.success("User record deleted")
      fetchUsers()
    } catch (error) {
      toast.error("Delete failed")
    }
  }

  const filteredUsers = users.filter(u => 
    u.name?.toLowerCase().includes(searchQuery.toLowerCase()) || 
    u.email?.toLowerCase().includes(searchQuery.toLowerCase())
  )

  return (
    <div className="space-y-6">
      {/* Header & Search */}
      <div className="bg-white p-6 border border-slate-100 rounded-sm shadow-sm flex flex-col md:flex-row justify-between items-center gap-4">
        <div>
          <h2 className="text-2xl font-serif font-bold text-slate-900">User Directory</h2>
          <p className="text-sm text-slate-500">Manage students and administrators across the platform.</p>
        </div>
        
        <div className="relative w-full md:w-80">
           <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
           <input 
             type="text" 
             placeholder="Search by name or email..." 
             value={searchQuery}
             onChange={e => setSearchQuery(e.target.value)}
             className="w-full pl-10 pr-4 py-2 border border-slate-100 bg-slate-50 outline-none focus:border-[#b48c36] transition-colors text-sm"
           />
        </div>
      </div>

      <div className="bg-white border border-slate-100 rounded-sm shadow-sm overflow-hidden">
        {loading ? (
          <div className="py-20 flex justify-center"><Loader2 className="animate-spin text-[#b48c36]" /></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-100">
                  <th className="px-6 py-4 text-[10px] font-bold uppercase tracking-widest text-slate-400">Identity</th>
                  <th className="px-6 py-4 text-[10px] font-bold uppercase tracking-widest text-slate-400">Access Level</th>
                  <th className="px-6 py-4 text-[10px] font-bold uppercase tracking-widest text-slate-400">Registration</th>
                  <th className="px-6 py-4 text-[10px] font-bold uppercase tracking-widest text-slate-400 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {filteredUsers.map((user) => (
                  <tr key={user.id} className="hover:bg-slate-50/50 transition-colors group">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-[#fcfaf7] border border-slate-100 flex items-center justify-center text-[#b48c36] font-serif font-bold relative">
                           {user.name?.charAt(0)}
                           {user.isVisible === false && <div className="absolute -top-1 -right-1 w-3 h-3 bg-rose-500 border-2 border-white rounded-full" />}
                        </div>
                        <div>
                          <p className="font-bold text-slate-900 text-sm flex items-center gap-2">
                            {user.name || 'Anonymous User'}
                            {user.isVisible === false && <span className="text-[8px] px-1.5 py-0.5 bg-rose-100 text-rose-500 rounded-full uppercase tracking-tighter">Hidden</span>}
                          </p>
                          <p className="text-xs text-slate-400 font-medium">{user.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                       <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest ${user.role === 'admin' ? 'bg-[#b48c36] text-white shadow-sm' : 'bg-slate-100 text-slate-500'}`}>
                          {user.role === 'admin' ? <Shield size={10} /> : <User size={10} />}
                          {user.role || 'Student'}
                       </span>
                    </td>
                    <td className="px-6 py-4">
                       <div className="flex items-center gap-2 text-slate-400">
                          <Calendar size={14} />
                          <span className="text-xs font-medium">
                             {user.createdAt?.toDate ? user.createdAt.toDate().toLocaleDateString() : 'New User'}
                          </span>
                       </div>
                    </td>
                    <td className="px-6 py-4 text-right">
                       <div className="flex justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button 
                            onClick={() => toggleAdmin(user)}
                            className="p-2 text-slate-400 hover:text-[#b48c36] hover:bg-white rounded-md transition-all shadow-sm border border-transparent hover:border-slate-100"
                            title={user.role === 'admin' ? 'Revoke Admin' : 'Make Admin'}
                          >
                             {user.role === 'admin' ? <UserX size={16} /> : <ShieldCheck size={16} />}
                          </button>
                          <button 
                            onClick={() => toggleVisibility(user)}
                            className={`p-2 transition-colors rounded-md shadow-sm border border-transparent hover:bg-white hover:border-slate-100 ${user.isVisible === false ? 'text-rose-500' : 'text-slate-400 hover:text-emerald-500'}`}
                            title={user.isVisible === false ? "Restore / Show User" : "Hide / Disable User"}
                          >
                             {user.isVisible === false ? <EyeOff size={16} /> : <Eye size={16} />}
                          </button>
                          <button 
                            onClick={() => deleteUser(user)}
                            className="p-2 text-slate-400 hover:text-rose-500 hover:bg-white rounded-md transition-all shadow-sm border border-transparent hover:border-slate-100"
                            title="Delete Record"
                          >
                             <Trash2 size={16} />
                          </button>
                       </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            
            {filteredUsers.length === 0 && (
              <div className="py-20 text-center">
                 <p className="text-slate-400 font-serif italic">No users matching your search discovered in the registry.</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
